// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2015
//
// ## Blank for Interactive Game
//
// Include description of object & useful info here, please!
import type {
  LayoutSettings,
  LayoutItem,
  InteractiveOptions,
  UserData,
} from './interactivePrototype';
import InteractivePrototype from './interactivePrototype';
import type { MelodicContourQuestion } from '../uTheory.Skills/melodicContourQuestions';
import MelodicContourQuestions from '../uTheory.Skills/melodicContourQuestions';
import utils from '../utils';
import ProtoNotation from '../uTheory.Notation/protoNotation';

interface MelodicCountourInteractiveLayout extends LayoutSettings {
  protoNotation: LayoutItem;
}

interface MelodicContourInteractiveOptions extends InteractiveOptions {
  level: number;
}

/**
 * Identify the right contour of an aural melody from among 4 choices
 */
export default class MelodicContour extends InteractivePrototype {
  declare layout: MelodicCountourInteractiveLayout;

  protoNotationDiv: HTMLDivElement;

  brs: HTMLBRElement[];

  pns: ProtoNotation[];

  declare options: MelodicContourInteractiveOptions;

  private _timeout: number;

  declare questions: MelodicContourQuestion[];

  declare currentQuestion: MelodicContourQuestion;

  constructor(
    displayDiv: HTMLDivElement,
    options: MelodicContourInteractiveOptions,
    userData: UserData
  ) {
    // console.log('options', options);
    // console.log('userData', userData);
    super(displayDiv, options, userData);
    this.layout = {
      protoNotation: {
        percent: 85,
        visible: true,
      },
    };
    this.init(displayDiv, options, userData);
  }

  init(displayDiv: HTMLDivElement, options: MelodicContourInteractiveOptions, userData: UserData) {
    this.settings = {
      instructionsPanel: {
        innerHTML:
          '<p>You will hear a series of notes, ' +
          'click the shape that most closely matches the notes.</p>',
        callback: this.playMusic,
        //          thisArg: this
      },
      instructions: true,
      checkMark: {},
      player: {},
      progressTracker: true,
    };

    return super.init(displayDiv, options, userData);
  }

  createCustomDivs() {
    this.createDiv('protoNotationDiv', {
      overflow: 'hidden',
      textAlign: 'center',
    });
    this.layout.protoNotation.div = this.protoNotationDiv;
    this.layout.protoNotation.callback = this.resizeProtoNotation.bind(this);

    this.brs = [];
    const makeProtoNotation = (arr) => {
      const pn = new ProtoNotation(arr || [0, 66, 33, 100]);
      pn.svg.style.width = '45%';
      pn.styleAsButton();
      this.protoNotationDiv.appendChild(pn.svg);
      const br = document.createElement('br');
      br.style.display = 'none';
      this.brs.push(br);
      this.protoNotationDiv.appendChild(br);
      return pn;
    };

    const pns = [
      [0, 33, 66, 100],
      [100, 66, 0, 33],
      [33, 66, 0, 100],
      [100, 33, 0, 0],
    ].map(makeProtoNotation);

    this.pns = pns;
  }

  resizeProtoNotation(heightAvailable: number) {
    this.protoNotationDiv.style.height = '';

    const width = this.protoNotationDiv.clientWidth;
    let percent = 40;
    const ratios = {
      square: 1.25,
      wide: 4,
      tall: 0.25,
    };
    const ratio = utils.closest(width / heightAvailable, ratios);

    this.brs.forEach((br) => {
      br.style.display = 'none';
    });

    switch (ratio) {
      // if square-ish, size at about 40% w/ some padding
      case 'square':
        percent = 45;
        this.brs[1].style.display = 'block';
        break;
      // if wide landscape, size at about 20% w/ padding
      case 'wide':
        percent = 20;
        break;
      // if very tall, size at 80%;
      case 'tall':
        percent = 85;
        this.brs[0].style.display = 'block';
        this.brs[1].style.display = 'block';
        this.brs[2].style.display = 'block';
        break;
    }

    const pns = this.pns;
    const setPercent = function (pcnt) {
      pns.forEach((pn) => {
        pn.svg.style.width = `${pcnt}%`;
      });
    };

    setPercent(percent);

    while (heightAvailable - this.protoNotationDiv.clientHeight < 0) {
      percent -= 2;
      setPercent(percent);
    }

    this.protoNotationDiv.style.height = `${this.protoNotationDiv.clientHeight}px`;
    return heightAvailable - this.protoNotationDiv.clientHeight;
  }

  generateQuestions() {
    const maxQuestions = this.options.maxQuestions || 8;
    const qg = new MelodicContourQuestions();

    let level: number;
    if (this.options.level) {
      level = this.options.level;
    } else if (this.options.skills && this.userData && this.userData.progress) {
      level = MelodicContourQuestions.getRecommendedLevel(this.userData.progress)[0];
    } else if (this.options.skills) {
      // Pick an appropriate level for them to work on:
      const levels = MelodicContourQuestions.skillsToLevels(this.options.skills)
        .reverse()
        .slice(-2);
      level = utils.shuffle(levels).slice(-1)[0];
    } else {
      level = Math.ceil(Math.random() * 6);
    }

    this.questions = qg.getQuestions(level, maxQuestions);
    this.questionPrompt = 'What contour do you hear?';
    // Generally, this sets four variables:
    // this.questions = [], an array of objects representing the questions
    // & answers:
    //       { text : string representation for display,
    //         answer : answer object for comparison,
    //         skills : integer or array of integers of unique_id's for skills
    //       }
    // this.questionPrompt, the text displayed to the user, with {question} replaced by...
    // this.maxQuestions = int.
    // this.questionNumber = 0;
  }

  addListeners() {
    // Code to set callbacks & communication to eventually check an answer.
  }

  newQuestion() {
    if (this._timeout) {
      window.clearTimeout(this._timeout);
      this.pns.forEach((pn) => {
        pn.stopFlashing();
      });
    }
    super.newQuestion();
    this.currentQuestion.choices.forEach((choice, index) => {
      this.pns[index].draw(choice);
      this.pns[index].listeners = [];
      this.pns[index].onclick(() => {
        this.checkAnswer(choice, index);
      }, this);
    }, this);
    if (this.questionNumber !== 0) {
      window.setTimeout(() => this.playMusic(), 500);
    }
  }

  gameOver() {
    super.gameOver();
    this.protoNotationDiv.style.display = 'none';
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
  }

  playMusic(delay = 0) {
    this.player.stop();
    const duration = 0.6;
    this.currentQuestion.notes.forEach((midi, index, arr) => {
      const startTime = duration * index + delay;
      let stopTime = index === arr.length - 1 ? duration * (index + 1) + 2 : duration * (index + 1);
      stopTime += delay;
      this.player.play(midi, { startTime, stopTime, velocity: 0.8 });
    }, this);
    if (!utils.isTouch()) return;
    this._timeout = window.setTimeout(() => {
      this.pns.forEach((pn) => {
        pn.startFlashing(600);
      });
    }, 3600);
  }

  displayInstructions() {
    super.displayInstructions();
  }

  checkAnswer(answer, pnIndex) {
    if (this.rightAnswerDelay) return;
    if (this.currentQuestion.answer === answer) {
      this.rightAnswer(500);
    } else {
      this.wrongAnswer(pnIndex);
    }
  }

  wrongAnswer(pnIndex) {
    this.pns[pnIndex].disable();
    this.playMusic(0.6);
    super.wrongAnswer();
  }

  static registry = {
    description: 'Identify the contour you hear',
    skills: [
      'contour_basic',
      'contour_high_low',
      'contour_high_medium_low_easy',
      'contour_high_medium_low_hard',
      'contour_four_level_easy',
      'contour_four_level_hard',
    ],
  };
}
